// RITA-1522 (Nutzungserfassung Phase 3d, Epic RITA-1496) — Fensterklassen für die Auswahl.
//
// Die Leiste lässt die Fensterklasse wählen, BEVOR eine Antwort vorliegt; sie braucht die Liste
// also im Client. Das ist bewusst eine zweite Quelle neben der einen Tabelle des Servers
// (apps/backend/src/services/usage/viewportClass.ts). Gegen das Driften steht kein Kommentar,
// sondern ein Vertragstest: viewportClasses.test.ts liest die Backend-Datei und vergleicht
// Labels, Reihenfolge und Grenzen.
//
// Die Abfrage kennt keine Klasse, sondern nur eine Breite (`viewportWidth`) — der Server
// klassifiziert selbst. Zu jeder Klasse gehört deshalb eine repräsentative Breite; sie liegt im
// halboffenen Bereich [min, max) der Klasse. Für '<600' ist das die Obergrenze minus eins (599)
// statt der 0: der Server lehnt 0 ab (assertViewportWidth, 1..65535).
//
// WAHRHEIT bleibt die Antwort: die Leiste zeigt `viewportClass` aus `UsageClickMap`, nie die
// hier gerechnete Klasse. Diese Liste steuert nur Auswahl und mitgeschickte Breite.

/** Eine wählbare Fensterklasse: Name wie im Backend und die Breite, mit der sie abgefragt wird. */
export interface ViewportClassOption {
  label: string;
  width: number;
}

/** Die sechs Klassen in der Reihenfolge des Backends (aufsteigende Breite). */
export const VIEWPORT_CLASSES: readonly ViewportClassOption[] = [
  { label: '<600', width: 599 },
  { label: '600-899', width: 600 },
  { label: '900-1199', width: 900 },
  { label: '1200-1439', width: 1200 },
  { label: '1440-1919', width: 1440 },
  { label: '>=1920', width: 1920 },
];

/** Untergrenzen in derselben Reihenfolge — abgeleitet, damit es nur EINE Zahlenliste gibt. */
const untergrenze = (stelle: number): number => (stelle === 0 ? 0 : VIEWPORT_CLASSES[stelle].width);

/**
 * Klasse der eigenen Fensterbreite (Vorgabe der Auswahl). Unbrauchbare Werte (0, NaN) fallen in
 * die unterste Klasse — dieselbe Regel wie viewportClassBoundOf im Backend, das Werte unterhalb
 * der ersten Klasse ebenfalls dort einsortiert.
 */
export const eigeneKlasse = (innerWidth: number): string => {
  const breite = Number.isFinite(innerWidth) ? Number(innerWidth) : 0;
  let gefunden = VIEWPORT_CLASSES[0].label;
  VIEWPORT_CLASSES.forEach((klasse, stelle) => {
    if (breite >= untergrenze(stelle)) gefunden = klasse.label;
  });
  return gefunden;
};

/** Die Breite, mit der eine Klasse abgefragt wird; unbekannter Name → unterste Klasse. */
export const breiteDerKlasse = (label: string): number =>
  (VIEWPORT_CLASSES.find((klasse) => klasse.label === label) ?? VIEWPORT_CLASSES[0]).width;
